<template>
  <div class="task-input-wrapper task-composer" :class="{ 'task-input-wrapper--entrance': entrance, 'task-composer--stacked': layout === 'stacked', 'task-composer--inline': isInline, 'task-composer--voice': voiceEnabled || voiceBlocked }" @keydown.capture="onRootKeydownCapture">
    <!-- variant="classic" (default): My Tasks classic + the Patient Profile Create Task panel. This branch
         comes FIRST and is unchanged — cypress/node-tests/task-composer-layout-contract.test.js §2 reads it. -->
    <template v-if="!isInline">
    <at at="#" :members="patients" name-key="PatientWithDOB" v-if="patients" ref="patientat">
      <template v-slot:embeddedItem="s">
        <span>
          <span :data-teamio-patientid="s.current.PatNum" class="badge mr-xs badge-secondary badge-pill mt-1">
            <router-link tabindex="-1" :to="{ name: 'patientprofile', params: { patientID: siteID + '-' + s.current.PatNum }}">{{ s.current.Patient }}</router-link>
          </span>
        </span>
      </template>
      <at at="@" :members="activeEmployees" name-key="Name" v-model="newTodoContent" v-if="employees" ref="empat">
        <template slot="item" slot-scope="s"><span tabindex="-1">{{ s.item.Name }}</span></template>
        <template slot="embeddedItem" slot-scope="s">
          <span><span tabindex="-1" :data-teamio-employeeid="s.current.EmployeeID" class="badge mr-xs badge-primary badge-pill mt-1">{{ s.current.Name + " " }}</span></span>
        </template>
        <div class="task-input-section">
          <div class="task-input-container">
            <div class="ToDo__addNew newtodo" id="newtodo" ref="newtodo" @input="onInput" :placeholder="placeholder" contenteditable />
            <DatePicker v-model="selectedDueDate" :format="dateFormatStr" input-class="due-date-input" placeholder="Due date" class="due-date-picker" lang="en" :append-to-body="dropdownsInBody" :popup-style="dueDatePopupStyle" :editable="false" />
            <multiselect v-model="selectedTags" :options="availableTags || []" :multiple="true" :taggable="true" placeholder="Add tags..." @tag="addTag" class="tag-input" :appendToBody="dropdownsInBody" />
            <b-button variant="success" type="submit" class="add-task-button" :disabled="saving" @click="saveTodo">{{ saving ? 'Adding...' : 'Add Task' }}</b-button>
            <slot name="actions"></slot>
          </div>
        </div>
        <DatePreview v-if="false && detectedDateInfo.hasDate" :date="detectedDateInfo.date" />
      </at>
    </at>
    </template>
    <!-- variant="inline" (the redesigned Tasks page, design "Tasks Page Options" 2a): ONE input with a
         leading ⊕, chips under it while focused / typed in, Enter adds, Escape clears. No due-date
         picker, tags box or button — tags are edited in the drawer. Same save path as classic. -->
    <template v-else>
    <at at="#" :members="patients" name-key="PatientWithDOB" v-if="patients" ref="patientat" class="at-patients" @insert="refreshDraft">
      <template v-slot:item="s">
        <span class="tc-at-item" tabindex="-1">
          <span class="tc-at-item__name">{{ s.item.PatientWithDOB }}</span>
          <span v-if="openCounts" class="tc-at-item__sub" data-test="task-composer-open-count">{{ patientOpenLabel(s.item) }}</span>
        </span>
      </template>
      <template v-slot:embeddedItem="s">
        <span>
          <span :data-teamio-patientid="s.current.PatNum" class="badge mr-xs badge-secondary badge-pill mt-1">
            <router-link tabindex="-1" :to="{ name: 'patientprofile', params: { patientID: siteID + '-' + s.current.PatNum }}">{{ s.current.Patient }}</router-link>
          </span>
        </span>
      </template>
      <at at="@" :members="activeEmployees" name-key="Name" v-model="newTodoContent" v-if="employees" ref="empat" class="at-staff" @insert="refreshDraft">
        <template slot="item" slot-scope="s">
          <span class="tc-at-item" tabindex="-1">
            <span class="tc-at-item__name">{{ s.item.Name }}</span>
            <span v-if="openCounts" class="tc-at-item__sub" data-test="task-composer-open-count">{{ staffOpenLabel(s.item) }}</span>
          </span>
        </template>
        <template slot="embeddedItem" slot-scope="s">
          <span><span tabindex="-1" :data-teamio-employeeid="s.current.EmployeeID" class="badge mr-xs badge-primary badge-pill mt-1">{{ s.current.Name + " " }}</span></span>
        </template>
        <div class="task-input-section">
          <div class="task-input-container">
            <div
              class="ToDo__addNew newtodo"
              id="newtodo"
              ref="newtodo"
              data-test="task-composer-input"
              :placeholder="placeholder"
              contenteditable
              @input="onInput"
              @keydown.enter="onInlineEnter"
              @keydown.esc="onInlineEscape"
              @focus="onInlineFocus"
              @blur="onInlineBlur"
            />
            <span class="tc-inline__lead" aria-hidden="true">
              <svg viewBox="0 0 20 20" width="18" height="18" focusable="false"><circle cx="10" cy="10" r="7.25" fill="none" stroke="currentColor" stroke-width="1.6" /><path d="M10 6.75v6.5M6.75 10h6.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
            </span>
            <!-- Voice prototype (2026-10-08): Super Admin only, and only where this computer can do
                 on-device speech (checkVoiceSupport). Speech is recognised on this computer — see toggleVoice(). -->
            <button
              v-if="voiceEnabled || voiceBlocked"
              type="button"
              class="tc-inline__mic"
              :class="{ 'tc-inline__mic--on': voicePhase === 'listening' }"
              data-test="task-composer-mic"
              :aria-label="voiceListening ? 'Stop dictation' : 'Talk to create a task'"
              :title="voiceListening ? 'Stop dictation (or press Space)' : voiceBlocked ? 'Talk to create a task' : 'Talk to create a task — or press Space (hold to talk). Speech is processed on this computer only.'"
              :disabled="voiceBusy"
              @mousedown.prevent
              @click="toggleVoice"
            >
              <svg viewBox="0 0 20 20" width="17" height="17" aria-hidden="true" focusable="false"><rect x="7" y="2.75" width="6" height="9.5" rx="3" fill="none" stroke="currentColor" stroke-width="1.6" /><path d="M4.5 9.5a5.5 5.5 0 0 0 11 0M10 15v2.5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
            </button>
            <slot name="actions"></slot>
          </div>
        </div>
        <!-- The dictation status pill: preparing → listening → processing → done (or error / info). -->
        <div
          v-if="(voiceEnabled || voiceBlocked) && voiceTitle"
          class="tc-voice"
          :class="'tc-voice--' + (voicePhase || 'info')"
          data-test="task-composer-voice-status"
          :data-phase="voicePhase || 'info'"
          role="status"
          aria-live="polite"
        >
          <span class="tc-voice__icon" aria-hidden="true">
            <span v-if="voicePhase === 'listening'" class="tc-voice__dot" />
            <span v-else-if="voicePhase === 'preparing' || voicePhase === 'processing'" class="tc-voice__spinner" />
            <svg v-else-if="voicePhase === 'done'" viewBox="0 0 16 16" width="14" height="14" focusable="false"><path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
            <svg v-else viewBox="0 0 16 16" width="14" height="14" focusable="false"><circle cx="8" cy="8" r="6.25" fill="none" stroke="currentColor" stroke-width="1.5" /><path d="M8 7.25v4M8 4.75v.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
          </span>
          <span class="tc-voice__body">
            <span class="tc-voice__head">
              <strong class="tc-voice__title" data-test="task-composer-voice-phase">{{ voiceTitle }}</strong>
              <span v-if="voiceHint" class="tc-voice__hint">{{ voiceHint }}</span>
            </span>
            <span v-if="voiceDetail" class="tc-voice__detail">{{ voiceDetail }}</span>
            <span v-for="(a, i) in voiceAmbiguous" :key="'amb-' + i + '-' + a.spoken" class="tc-voice__warn" data-test="task-composer-voice-ambiguous">
              <span class="tc-voice__ask">Which {{ a.kind === 'staff' ? 'team member' : 'patient' }} is “{{ a.spoken }}”?</span>
              <button
                v-for="opt in (a.options || [])"
                :key="opt.PatNum || opt.EmployeeID"
                type="button"
                class="tc-voice__pick"
                data-test="task-composer-voice-pick"
                @mousedown.prevent
                @click="pickVoiceCandidate(a, opt)"
              >{{ opt.label }}</button>
              <span v-if="a.total > (a.options || []).length" class="tc-voice__more">+{{ a.total - (a.options || []).length }} more — type {{ a.kind === 'staff' ? '@' : '#' }} to search</span>
            </span>
          </span>
        </div>
        <div v-if="chipsVisible" class="tc-inline__chips" data-test="task-composer-chips">
          <template v-for="chip in inlineChips">
            <span v-if="chip.key === 'due'" :key="chip.key" class="tc-chip-host">
              <button
                type="button"
                class="tc-chip"
                :class="{ 'tc-chip--set': !chip.dim }"
                data-test="task-composer-chip"
                :data-chip="chip.key"
                :aria-label="`Due ${chip.value} — change the due date`"
                @mousedown.prevent
                @click="openDuePicker"
              >
                <svg class="tc-chip__icon" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false"><rect x="2.5" y="3.5" width="11" height="10" rx="2" fill="none" stroke="currentColor" stroke-width="1.4" /><path d="M2.5 6.75h11M5.5 2v3M10.5 2v3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" /></svg>
                <span class="tc-chip__label">{{ chip.label }}</span>
                <span class="tc-chip__value">{{ chip.value }}</span>
              </button>
              <input
                ref="dueInput"
                type="date"
                class="tc-chip__date"
                data-test="task-composer-due-input"
                tabindex="-1"
                aria-label="Due date"
                :value="inlineDueYmd"
                @change="onDueInputChange"
                @blur="dueOpen = false"
              >
            </span>
            <button
              v-else-if="chip.key === 'assign'"
              :key="chip.key"
              type="button"
              class="tc-chip"
              :class="{ 'tc-chip--set': !chip.dim }"
              data-test="task-composer-chip"
              :data-chip="chip.key"
              :aria-label="`Assign to ${chip.value} — add someone`"
              @mousedown.prevent
              @click="startMention"
            >
              <svg class="tc-chip__icon" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false"><circle cx="8" cy="5.5" r="2.75" fill="none" stroke="currentColor" stroke-width="1.4" /><path d="M2.75 13.5c.6-2.5 2.7-4 5.25-4s4.65 1.5 5.25 4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" /></svg>
              <span class="tc-chip__label">{{ chip.label }}</span>
              <span class="tc-chip__value">{{ chip.value }}</span>
            </button>
            <span
              v-else
              :key="chip.key"
              class="tc-chip"
              :class="{ 'tc-chip--set': !chip.dim }"
              data-test="task-composer-chip"
              :data-chip="chip.key"
            >
              <svg v-if="chip.key === 'repeat'" class="tc-chip__icon" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false"><path d="M3 7.25a5 5 0 0 1 8.6-3.25M13 8.75a5 5 0 0 1-8.6 3.25" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" /><path d="M11.75 1.75v2.5h-2.5M4.25 14.25v-2.5h2.5" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" /></svg>
              <svg v-else class="tc-chip__icon" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" focusable="false"><circle cx="8" cy="5.5" r="2.75" fill="none" stroke="currentColor" stroke-width="1.4" /><path d="M2.75 13.5c.6-2.5 2.7-4 5.25-4s4.65 1.5 5.25 4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" /></svg>
              <span class="tc-chip__label">{{ chip.label }}</span>
              <span class="tc-chip__value">{{ chip.value }}</span>
            </span>
          </template>
          <span class="tc-inline__hint" data-test="task-composer-hint"><span aria-hidden="true">↵</span> Enter to add</span>
        </div>
      </at>
    </at>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import _ from 'lodash';
import moment from 'moment-timezone';
import DatePicker from 'vue2-datepicker';
import Multiselect from 'vue-multiselect';
import At from '@/pages/TaskList/vueat/At.vue';
import DatePreview from '@/pages/TaskList/components/DatePreview.vue';
import simpleParser from '@/utils/chrono-simple';
import { DEFAULT_TIMEZONE, dateFormatFor, validateDraft, buildNewTask } from '@/utils/taskDraft';
import { buildPatientBadgeHTML, ensurePatientTagged, hasTaskText, getTaggedPatientIDs } from '@/utils/taskPatients';
import { getUniqueLinkedEmployeeIDs } from '@/utils/taskMentions';
import { repeatLabel } from '@/utils/tasks/myDay.js';
import { buildComposerChips, openCountLabel } from '@/utils/tasks/composerChips.js';
import { extractApiError } from '@/utils/apiError';
import { resolveVoiceTask, staffPhrases, stripFillers, buildEmployeeBadgeHTML } from '@/utils/voiceTask';
import { voiceBrowserBlock } from '@/utils/browserDetection';

// ---- Voice prototype (2026-10-08) ----
// SUPER ADMIN ONLY for now (Saumil, 2026-10-08), and only where this computer can actually do it:
// see checkVoiceSupport().
const VOICE_ROLE = 'Super Admin';
// processLocally: true ⇒ the browser must recognise speech ON THIS COMPUTER and may NOT fall back to
// a cloud service (patient names are spoken). Every available()/install() call uses these options.
const VOICE_OPTIONS = { langs: ['en-US'], processLocally: true };
// available() answers that mean "this computer can do on-device English" (downloadable = the speech
// pack is fetched on first use). Anything else — 'unavailable', an error — hides the mic.
const VOICE_USABLE = ['available', 'downloadable', 'downloading'];
// install() reports no progress and may never settle (blocked download); wait this long, then say so.
const VOICE_INSTALL_TIMEOUT_MS = 90000;
const CHROME_REQUIRED = 'Voice typing requires Google Chrome — if you\'re using another browser, switch to Chrome.';
/** install() or 'timeout' — the timer is always cleared, so nothing is left running. */
function installWithTimeout(SR, ms) {
  let timer;
  const timeout = new Promise((resolve) => { timer = setTimeout(() => resolve('timeout'), ms); });
  return Promise.race([SR.install(VOICE_OPTIONS), timeout]).finally(() => clearTimeout(timer));
}
/** A microphone is plugged in. Unknowable (no enumerateDevices / it throws) ⇒ assume yes. */
async function hasMicrophone() {
  const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : null;
  if (!md || typeof md.enumerateDevices !== 'function') return true;
  try {
    const devices = await md.enumerateDevices();
    return devices.some(d => d && d.kind === 'audioinput');
  } catch (_) {
    return true;
  }
}
/** Brave keeps Chrome's user-agent string; only `navigator.brave` gives it away. */
function isBraveBrowser() {
  return typeof navigator !== 'undefined' && !!navigator.brave && typeof navigator.brave.isBrave === 'function';
}
function speechCtor() {
  return (typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition)) || null;
}
/** On-device recognition exists (Chrome/Edge 139+). Older browsers only have the cloud path → no mic. */
function localSpeechSupported() {
  const SR = speechCtor();
  return !!(SR && typeof SR.available === 'function' && typeof SR.install === 'function' && SR.prototype && 'processLocally' in SR.prototype);
}
// Space shortcut: held at least this long = push-to-talk (release finishes); shorter = a tap, which
// keeps listening hands-free until the next Space.
const VOICE_HOLD_MS = 400;
// After stop(), the on-device recognizer finalises the last words and then fires onend. If it never
// does, "Processing…" must not spin forever: finish with what was heard after this long.
const VOICE_FINISH_TIMEOUT_MS = 8000;
// How strongly the recognizer leans toward a staff name hint (spec range 0–10; 1 = neutral).
// Moderate on purpose: too high and ordinary words start turning into names.
const VOICE_NAME_BOOST = 3;
function isSpaceKey(e) {
  return e.code === 'Space' || e.key === ' ' || e.key === 'Spacebar';
}
/** Somewhere Space already means something: a text field, an editor, a button, a select. */
function spaceBelongsToTarget(t) {
  if (!t || t === document.body || t === document.documentElement) return false;
  if (t.isContentEditable) return true;
  if (typeof t.closest === 'function' && t.closest('[contenteditable]:not([contenteditable="false"])')) return true;
  const tag = (t.tagName || '').toUpperCase();
  if (['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'OPTION'].includes(tag)) return true;
  const role = t.getAttribute && t.getAttribute('role');
  return ['button', 'checkbox', 'switch', 'menuitem', 'option', 'tab', 'textbox', 'combobox'].includes(role);
}

// chrono-simple daysOfWeek index → the 'ddd' name buildNewTask stores in repeatOn.
const REPEAT_DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// The design defers the focus-out so a click on a chip lands before the chips row hides.
const BLUR_DELAY_MS = 150;

/**
 * The ONE task composer, extracted from ToDo.vue (2026-09-02). My Tasks and the
 * Patient Profile "Create Task" panel mount this same component; the only thing
 * that differs is a seeded patient chip and where the dropdowns append.
 *
 * Store access is mapped DIRECTLY (no 28KB global mixin) — same pattern as
 * Kanban/components/AddTaskPanel.vue — so this component can mount cheaply.
 */
export default {
  name: 'TaskComposer',
  components: { At, DatePreview, DatePicker, Multiselect },
  props: {
    // The patient this composer is scoped to (Patient Profile panel), or null (My Tasks).
    seedPatient: { type: Object, default: null },
    // When seeded, guarantee the saved task is tagged with the patient even if the
    // user deleted the chip. My Tasks passes false via seedPatient=null anyway.
    enforcePatient: { type: Boolean, default: true },
    maxSortId: { type: Number, default: 0 },
    availableTags: { type: Array, default: () => [] },
    autofocus: { type: Boolean, default: false },
    entrance: { type: Boolean, default: false },
    placeholder: {
      type: String,
      default: "Add your task here... (use @ to assign, # for patients, and natural language for dates like 'by Tuesday' or 'every Monday')",
    },
    // true → body-appended dropdowns (My Tasks). The slide-out panel passes false
    // because a body-appended dropdown can render BEHIND the panel.
    dropdownsInBody: { type: Boolean, default: true },
    // 'row' (default, My Tasks): the CSS decides per CONTAINER width — one wide row while
    // .task-input-container is >= --composer-row-min (1200px), otherwise the editor owns the first
    // row and the controls wrap below (see the style block). 'stacked' (the 800px Create Task panel)
    // forces the stacked shape at any width and lets the tag box fill the second row.
    // History: 2026-09-02 the panel's editor collapsed to a sliver because the breakpoints keyed off
    // the viewport; 2026-09-11 the same starvation hit My Tasks on 1025–1440px viewports (a 200–410px
    // editor beside a 400–500px tag box) — hence the container-driven switch for every caller.
    layout: { type: String, default: 'row', validator: (v) => ['row', 'stacked'].includes(v) },
    // 'classic' (default): editor + due-date picker + tags + Add Task — every caller before 2026-10-07.
    // 'inline': the redesigned Tasks page's one-line composer (ToDo.vue's new branch): no picker, tags
    // or button; chips (Assign to · Patient · Due · Repeats) under it while focused or typed in; Enter
    // adds, Escape clears. Plan: .claude/plans/shiny-gliding-moth.md. `layout` does not apply to it.
    variant: { type: String, default: 'classic', validator: (v) => ['classic', 'inline'].includes(v) },
    // inline only: the due date a task gets when the text names none and no date was picked
    // (My day: start of today; Upcoming: start of tomorrow). null → buildNewTask's own default (today).
    defaultDue: { type: Date, default: null },
    // inline only: open-task counts for the @ / # suggestion rows ("3 open" / "No open tasks"), shaped
    // { staff: { [EmployeeID]: n }, patients: { [PatNum]: n } } — composerChips.js openTaskCounts().
    // null → the rows show the name only (the caller has no task list to count from).
    openCounts: { type: Object, default: null },
  },
  data() {
    return {
      selectedTags: [],
      selectedDueDate: null,
      newTodoContent: '',
      saving: false,
      detectedDateInfo: {
        hasDate: false,
        displayText: '',
        date: null,
        isRecurring: false,
        frequency: null,
        daysOfWeek: null,
        dayOfMonth: null,
        repeatOnMonth: null,
        isLastDayOfMonth: false,
      },
      // ---- inline variant only ----
      focused: false,       // the editor has focus (deferred off by BLUR_DELAY_MS)
      draftHtml: '',        // the editor's innerHTML, for the chips (refreshed on input and on <at> inserts)
      pickedDueDate: null,  // a date picked with the Due chip — beats a date in the text
      dueOpen: false,       // the Due chip's native picker is open — keep the chips row mounted
      // ---- voice prototype (inline only) ----
      // Super Admin AND this computer can do on-device speech — set by checkVoiceSupport() (async)
      voiceEnabled: false,
      voiceBlocked: '',     // this browser can't do voice typing: the message the mic shows when clicked
      voiceListening: false,
      voiceBusy: false,     // checking / downloading the on-device speech pack
      // The status pill under the editor: '' | preparing | listening | processing | done | error | info
      voicePhase: '',
      voiceStatus: '',      // the message for preparing / error / info
      voiceInterim: '',     // the words heard so far (live while listening; the final text once done)
      voiceVia: '',         // how dictation started: hold (Space held) | tap (Space tapped) | click
      voiceAmbiguous: [],   // names that matched more than one patient (left as text)
    };
  },
  computed: {
    ...mapState('customer', ['siteID', 'user', 'userID', 'employees', 'patients', 'settings']),
    ...mapGetters('customer', ['activeEmployees']),
    // Keyed-by-Property view of settings — the same shape ToDo.vue's allSettings has.
    allSettings() {
      return _.keyBy(this.settings, 'Property');
    },
    officeTimezone() {
      return this.allSettings?.GENERAL_TIMEZONE?.PropertyValue || DEFAULT_TIMEZONE;
    },
    dateFormatStr() {
      return dateFormatFor(this.officeTimezone);
    },
    // The badge-ready seed: the patient plus the site the badge links into.
    seedPatientForBadge() {
      return this.seedPatient ? { siteID: this.siteID, ...this.seedPatient } : null;
    },
    // vue2-datepicker aligns its popup from the VIEWPORT (input in the right half → popup right-aligned
    // to the input). Inline inside a right-docked slide-out that hangs the 250px calendar ~57px past the
    // panel's left edge, and `.slideout` (overflow-y:auto) clips it. Pin an inline popup to the input's
    // left edge; `popupStyle` is merged last in the lib ({...position, ...popupStyle}), so no !important.
    // Body-appended (My Tasks) keeps the library logic. `top` stays library-driven — it is input-relative
    // and the panel is viewport-tall. (2026-09-03)
    dueDatePopupStyle() {
      return this.dropdownsInBody ? {} : { left: '0px' };
    },

    // ---- inline variant ----
    isInline() {
      return this.variant === 'inline';
    },
    /** A Date → 'YYYY-MM-DD' in the office zone (the day buildNewTask will save), or null. */
    officeYmd() {
      const tz = this.officeTimezone;
      return (d) => {
        if (!d) return null;
        const m = moment.tz(d, tz);
        return m.isValid() ? m.format('YYYY-MM-DD') : null;
      };
    },
    hasDraft() {
      const html = this.draftHtml || '';
      return hasTaskText(html) || /data-teamio-(employee|patient)id=/.test(html);
    },
    chipsVisible() {
      return this.isInline && (this.focused || this.hasDraft || this.dueOpen);
    },
    /** The day the task will be due: picked / typed (selectedDueDate), else the caller's default. */
    inlineDueYmd() {
      return this.officeYmd(this.selectedDueDate) || this.officeYmd(this.defaultDue) || this.officeYmd(new Date());
    },
    inlineRepeatLabel() {
      const info = this.detectedDateInfo;
      if (!info || !info.hasDate || !info.isRecurring || !info.frequency) return '';
      const dow = Array.isArray(info.daysOfWeek) && info.daysOfWeek.length ? REPEAT_DAY_NAMES[info.daysOfWeek[0]] : undefined;
      // The same fields buildNewTask writes, read back through the row's own label (myDay.js).
      return repeatLabel({
        repeat: true,
        repeatFrequency: info.frequency,
        repeatOn: dow,
        repeatOnDay: info.dayOfMonth || (info.frequency === 'Monthly' ? 1 : undefined),
        repeatOnMonth: info.repeatOnMonth,
        repeatOnLastDayOfMonth: info.isLastDayOfMonth ? 'yes' : undefined,
      });
    },
    inlineChips() {
      const html = this.draftHtml || '';
      const employees = this.employees || [];
      const me = String(this.userID);
      const assigneeNames = getUniqueLinkedEmployeeIDs(html).map((id) => {
        const emp = employees.find(e => e && String(e.EmployeeID) === String(id));
        return { name: emp ? emp.Name : '', isMe: String(id) === me };
      });
      const patNums = getTaggedPatientIDs(html);
      let patientName = '';
      if (patNums.length) {
        const p = (this.patients || []).find(x => x && Number(x.PatNum) === patNums[0]);
        patientName = (p && p.Patient) || `#${patNums[0]}`;
        if (patNums.length > 1) patientName += ` +${patNums.length - 1}`;
      }
      return buildComposerChips({
        assigneeNames,
        patientName,
        dueYmd: this.officeYmd(this.selectedDueDate),
        defaultDueYmd: this.officeYmd(this.defaultDue),
        todayYmd: this.officeYmd(new Date()),
        repeatLabel: this.inlineRepeatLabel,
      });
    },

    // ---- voice status pill ----
    voiceTitle() {
      switch (this.voicePhase) {
        case 'listening': return 'Listening…';
        case 'processing': return 'Processing what you said…';
        case 'done': return 'Added to your task';
        default: return this.voiceStatus; // preparing / error / info carry their own message
      }
    },
    voiceDetail() {
      const words = this.voiceInterim ? `“${this.voiceInterim}”` : '';
      if (this.voicePhase === 'listening' || this.voicePhase === 'processing') return words;
      if (this.voicePhase === 'done') return `Heard: ${words} — check it, or click the task to edit it.`;
      return '';
    },
    /** What the keys do now — how to finish while listening; save / add / discard once done. */
    voiceHint() {
      if (this.voicePhase === 'done') return this.hasDraft ? 'Enter to save · Space to add more · Esc to discard' : '';
      if (this.voicePhase !== 'listening') return '';
      if (this.voiceVia === 'hold') return 'Let go of Space when you\'re done';
      if (this.voiceVia === 'tap') return 'Press Space when you\'re done';
      return 'Click the mic or press Space when you\'re done';
    },
  },
  watch: {
    seedPatient() {
      // Re-seed only while the editor holds no human text — never clobber a task in progress.
      const el = this.$refs.newtodo;
      if (!el) return;
      if (hasTaskText(el.innerHTML) === false) this.seedEditor();
    },
  },
  mounted() {
    this.checkVoiceSupport();
    if (this.seedPatient) {
      this.seedEditor();
      if (this.autofocus) this.setCaretAtEnd();
    } else if (this.autofocus) {
      this.setCaretAtBeginning();
    }
  },
  beforeDestroy() {
    if (this.blurTimer) clearTimeout(this.blurTimer);
    if (this._onVoiceKeydown) document.removeEventListener('keydown', this._onVoiceKeydown, true);
    if (this._onVoiceKeyup) document.removeEventListener('keyup', this._onVoiceKeyup, true);
    if (this._voiceFinishTimer) clearTimeout(this._voiceFinishTimer);
    if (this._voiceRec) {
      this._voiceRec.onend = null; // never write into a destroyed editor
      try { this._voiceRec.abort(); } catch (_) { /* already stopped */ }
      this._voiceRec = null;
    }
  },
  methods: {
    ...mapActions('tasks', ['addPersonalTask']),
    ...mapActions('customer', ['getSettings']),

    // "N open" at the right of an @ row (inline variant, openCounts set).
    staffOpenLabel(emp) {
      const map = (this.openCounts && this.openCounts.staff) || {};
      return openCountLabel(emp ? map[String(emp.EmployeeID)] : 0, 'staff');
    },

    // "N open" / "No open tasks" at the right of a # row (inline variant, openCounts set).
    patientOpenLabel(patient) {
      const map = (this.openCounts && this.openCounts.patients) || {};
      return openCountLabel(patient ? map[String(patient.PatNum)] : 0, 'patient');
    },

    onInput(e) {
      const el = this.$refs.newtodo;
      const target = e && e.target;
      const content =
        (target && (typeof target.innerText === 'string' ? target.innerText : target.textContent)) ||
        (el && (typeof el.innerText === 'string' ? el.innerText : el.textContent)) ||
        '';
      this.parseContentForDates(content);
      this.refreshDraft();
    },

    // ---- inline variant ----

    /** Re-read the editor for the chips (the editor's input, and both <at> inserts — those are DOM-only). */
    refreshDraft() {
      if (!this.isInline) return;
      const el = this.$refs.newtodo;
      this.draftHtml = el ? el.innerHTML : '';
    },

    /**
     * Capture phase on the root, i.e. BEFORE either <at> (they listen in capture on their own wraps,
     * inside this element). Remembers whether a mention panel was open when the key went down: At
     * closes its panel on Escape without stopping the event, so by the time the editor's own Escape
     * handler runs the panel already reads closed. (On Enter At stops the event while its panel is
     * open, so the editor never sees that Enter at all.)
     */
    onRootKeydownCapture() {
      this.mentionPanelWasOpen = this.isMentionPanelOpen();
    },

    isMentionPanelOpen() {
      const { empat, patientat } = this.$refs;
      return !!((empat && empat.atwho) || (patientat && patientat.atwho));
    },

    onInlineEnter(e) {
      if (e.shiftKey || e.isComposing) return;
      // At owns Enter while its panel is open (it picks the highlighted person / patient).
      if (this.mentionPanelWasOpen || this.isMentionPanelOpen()) return;
      e.preventDefault();
      if (this.saving) return;
      this.saveTodo();
    },

    onInlineEscape() {
      // Escape with a mention panel open only closes the panel (At does that itself).
      if (this.mentionPanelWasOpen || this.isMentionPanelOpen()) return;
      this.resetEditor();
    },

    onInlineFocus() {
      if (this.blurTimer) clearTimeout(this.blurTimer);
      this.blurTimer = null;
      this.focused = true;
      this.dueOpen = false;
    },

    onInlineBlur() {
      if (this.blurTimer) clearTimeout(this.blurTimer);
      this.blurTimer = setTimeout(() => {
        this.blurTimer = null;
        this.focused = false;
      }, BLUR_DELAY_MS);
    },

    /** Due chip: open the native date picker (Chrome / Edge / Safari 16+), else focus the input. */
    openDuePicker() {
      const r = this.$refs.dueInput;
      const input = Array.isArray(r) ? r[0] : r;
      if (!input) return;
      this.dueOpen = true;
      try {
        if (typeof input.showPicker === 'function') {
          input.showPicker();
          return;
        }
      } catch (_) { /* showPicker refused (no user activation / cross-origin) — fall back to focus */ }
      input.focus();
    },

    onDueInputChange(e) {
      const value = e && e.target ? e.target.value : '';
      this.dueOpen = false;
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
      // Start of that day in the office zone — the same instant buildNewTask normalises to.
      const picked = moment.tz(value, this.officeTimezone).startOf('day').toDate();
      this.pickedDueDate = picked;
      this.selectedDueDate = picked;
    },

    /** Assign to chip: drop " @" at the end of the text and let At open the people picker. */
    startMention() {
      const el = this.$refs.newtodo;
      if (!el) return;
      const text = el.textContent || '';
      const lead = text === '' || /\s$/.test(text) ? '@' : ' @'; // JS \s includes &nbsp;
      const node = document.createTextNode(lead);
      el.appendChild(node);
      el.focus();
      if (typeof window.getSelection === 'function' && typeof document.createRange === 'function') {
        try {
          const range = document.createRange();
          range.setStart(node, node.length);
          range.collapse(true);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(range);
        } catch (_) { /* selection not implemented in this environment */ }
      }
      // Bubbles through both <at> wraps (their @input → handleInput reads the "@" before the caret).
      el.dispatchEvent(new Event('input', { bubbles: true }));
    },

    // ---- Voice prototype (2026-10-08) ------------------------------------------------------------
    // Speech → text happens ON THIS COMPUTER (processLocally); text → @/# badges happens in
    // utils/voiceTask.js against the lists already in memory. The result lands in the editor as a
    // draft — the user reviews it and presses Enter, exactly like a typed task. Nothing auto-saves.

    /**
     * Show the mic (and arm the Space / Enter / Escape keys) only when ALL hold:
     *   1. the redesigned Tasks page (inline variant) and a Super Admin;
     *   2. the browser has ON-DEVICE recognition (Chrome / Edge 139+: processLocally + available());
     *   3. available() says this computer can do on-device English (no user click needed to ask);
     *   4. a microphone is plugged in.
     * Anyone else never sees a control they cannot use.
     */
    async checkVoiceSupport() {
      if (!this.isInline || !this.user || this.user.Role !== VOICE_ROLE) return;
      // Brave / Firefox / Safari: show the mic, and a click says why it can't work (no Space shortcut armed).
      const blocked = voiceBrowserBlock(typeof navigator !== 'undefined' ? navigator.userAgent : '', isBraveBrowser());
      if (blocked) {
        this.voiceBlocked = blocked;
        return;
      }
      if (!localSpeechSupported()) return;
      let status = 'unavailable';
      try { status = await speechCtor().available(VOICE_OPTIONS); } catch (_) { /* treated as unavailable */ }
      if (!VOICE_USABLE.includes(status)) return;
      if (!(await hasMicrophone())) return;
      if (this._isDestroyed || this._isBeingDestroyed) return;
      this.voiceEnabled = true;
      // Capture phase: while dictating, a Space in the composer's own editor must finish the
      // dictation instead of typing a space.
      this._onVoiceKeydown = (e) => this.onVoiceKeydown(e);
      this._onVoiceKeyup = (e) => this.onVoiceKeyup(e);
      document.addEventListener('keydown', this._onVoiceKeydown, true);
      document.addEventListener('keyup', this._onVoiceKeyup, true);
    },

    /** One place that moves the status pill: phase + (for preparing / error / info) its message. */
    setVoicePhase(phase, message = '') {
      this.voicePhase = phase;
      this.voiceStatus = message;
    },

    async toggleVoice() {
      if (this.voiceBlocked) {
        this.setVoicePhase('error', this.voiceBlocked);
        return;
      }
      if (this.voiceListening) {
        this.stopVoice();
        return;
      }
      if (this.voiceBusy) return;
      this._voiceCancelPending = false;
      if (!this.voiceVia) this.voiceVia = 'click';
      const SR = speechCtor();
      if (!SR || !localSpeechSupported()) {
        this.setVoicePhase('error', `On-device speech isn't available in this browser. ${CHROME_REQUIRED}`);
        return;
      }
      this.voiceBusy = true;
      this.voiceInterim = '';
      this.voiceAmbiguous = [];
      this.setVoicePhase('preparing', 'Getting the microphone ready…');
      try {
        let status = 'unavailable';
        try { status = await SR.available(VOICE_OPTIONS); } catch (_) { /* treated as unavailable */ }
        if (status === 'downloadable' || status === 'downloading') {
          // install() needs the click's user activation — it is called straight after available().
          this.setVoicePhase('preparing', 'Downloading the speech pack to this computer (one time only)…');
          let installed = false;
          try { installed = await installWithTimeout(SR, VOICE_INSTALL_TIMEOUT_MS); } catch (_) { installed = false; }
          if (installed === 'timeout') {
            this.setVoicePhase('error', `The speech pack is taking too long to download. ${CHROME_REQUIRED} Otherwise, click the mic to try again.`);
            return;
          }
          if (!installed) {
            this.setVoicePhase('error', `Couldn't download the speech pack. ${CHROME_REQUIRED} Otherwise, click the mic to try again.`);
            return;
          }
        } else if (status !== 'available') {
          this.setVoicePhase('error', `On-device speech isn't available on this computer, so dictation is off. Nothing was sent anywhere. ${CHROME_REQUIRED}`);
          return;
        }
        // Space was released before listening could start (e.g. the one-time download): don't open the mic.
        if (this._voiceCancelPending) {
          this._voiceCancelPending = false;
          this.setVoicePhase('info', 'Speech is ready. Hold Space (or click the mic) and talk.');
          return;
        }
        this.startVoice(SR);
      } finally {
        this.voiceBusy = false;
        if (!this.voiceListening) this.voiceVia = '';
      }
    },

    startVoice(SR) {
      const rec = new SR();
      rec.processLocally = true;
      // Fail closed: a browser that ignores the property could quietly use its cloud service.
      if (rec.processLocally !== true) {
        this.setVoicePhase('error', `On-device speech isn't available in this browser, so dictation is off. ${CHROME_REQUIRED}`);
        return;
      }
      rec.lang = 'en-US';
      rec.continuous = true;
      rec.interimResults = true;
      this._voiceFinal = '';
      this._voiceError = '';
      this._voiceRetryPlain = false;
      // Name hints: tell the recognizer this site's staff names so "Nilay" comes back as "Nilay".
      // Optional — a browser without phrase support (or one that rejects them) still dictates.
      const Phrase = typeof window !== 'undefined' ? window.SpeechRecognitionPhrase : null;
      const hints = this._voiceNoPhrases ? [] : staffPhrases(this.activeEmployees);
      if (hints.length && Phrase && 'phrases' in rec) {
        try { rec.phrases = hints.map(p => new Phrase(p, VOICE_NAME_BOOST)); } catch (_) { /* no hints */ }
      }

      rec.onresult = (event) => {
        let finalT = '';
        let interimT = '';
        for (let i = 0; i < event.results.length; i++) {
          const r = event.results[i];
          if (r.isFinal) finalT += r[0].transcript + ' ';
          else interimT += r[0].transcript;
        }
        this._voiceFinal = finalT;
        this.voiceInterim = (finalT + interimT).trim();
      };
      rec.onerror = (event) => {
        const code = event && event.error;
        if (code === 'not-allowed' || code === 'service-not-allowed') {
          this._voiceError = 'Microphone access is blocked. Allow the mic for this site in your browser settings, then try again.';
        } else if (code === 'audio-capture') {
          this._voiceError = 'No microphone was found. Check that a mic is connected and not in use by another app.';
        } else if (code === 'phrases-not-supported') {
          // This computer's speech pack can't take name hints: listen again without them.
          this._voiceNoPhrases = true;
          this._voiceRetryPlain = true;
        } else if (code === 'language-not-supported') {
          this._voiceError = 'The speech pack isn\'t ready on this computer yet. Click the mic to try again.';
        } else if (code !== 'no-speech' && code !== 'aborted') {
          this._voiceError = `Dictation stopped (${code}). Try again or type instead.`;
        }
        // onend follows every error and finishes up.
      };
      rec.onend = () => this.finishVoice();

      this._voiceRec = rec;
      this.voiceListening = true;
      this.setVoicePhase('listening');
      try {
        rec.start();
      } catch (_) {
        this.voiceListening = false;
        this._voiceRec = null;
        this.setVoicePhase('error', 'Couldn\'t start the microphone. Try again or type instead.');
      }
    },

    /**
     * The recognizer has ended (or the processing timeout fired): put what was heard into the draft.
     * Falls back to the last interim words when no final result arrived — a quick release often
     * ends before the recognizer marks anything final.
     */
    finishVoice() {
      if (this._voiceFinishTimer) clearTimeout(this._voiceFinishTimer);
      this._voiceFinishTimer = null;
      if (this._voiceRec) this._voiceRec.onend = null;
      this._voiceRec = null;
      this.voiceListening = false;
      if (this._voiceRetryPlain) {
        this._voiceRetryPlain = false;
        const SR = speechCtor();
        if (SR) { this.startVoice(SR); return; }
      }
      this.voiceVia = '';
      // Without "um" / "uh": the draft and the "Heard:" line get the cleaned words, and a
      // dictation that was only "um" counts as nothing heard.
      const heard = stripFillers((this._voiceFinal || '').trim() || this.voiceInterim || '');
      this._voiceFinal = '';
      if (heard) {
        this.applyVoiceTranscript(heard);
      } else if (this._voiceError) {
        this.setVoicePhase('error', this._voiceError);
      } else {
        this.setVoicePhase('error', 'Didn\'t catch that. Hold Space (or click the mic) and try again.');
      }
      this._voiceError = '';
    },

    /**
     * Space shortcut (document, capture phase). Not dictating: Space starts it — unless Space already
     * means something where the focus is (a field, the editor itself, a button) or a dialog is open.
     * Dictating: Space finishes a TAP-started dictation; a held Space just keeps talking.
     */
    onVoiceKeydown(e) {
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey || e.isComposing) return;
      const el = this.$refs.newtodo;
      // A dictated draft is waiting with the editor unfocused: Enter saves it, Escape discards it —
      // unless the keypress belongs to a field (incl. the editor, once clicked into) or a dialog.
      if ((e.key === 'Enter' || e.key === 'Escape') && this.voicePhase === 'done' && this.hasDraft) {
        if (e.repeat || spaceBelongsToTarget(e.target) || document.querySelector('.modal.show')) return;
        e.preventDefault();
        if (e.key === 'Enter') {
          if (!this.saving) this.saveTodo();
        } else {
          this.resetEditor();
        }
        return;
      }
      if (!isSpaceKey(e)) return;
      if (this.voiceListening || this.voiceBusy) {
        // Another field keeps its Space; the composer's own editor does not (it would type a space).
        if (spaceBelongsToTarget(e.target) && e.target !== el) return;
        e.preventDefault();
        if (e.repeat || this._voiceSpaceDownAt != null) return; // holding: keep talking
        if (this.voiceListening) this.stopVoice();
        else this._voiceCancelPending = true;
        return;
      }
      if (e.repeat || !el || !el.isConnected) return;
      // An EMPTY composer editor counts as "not typing": after Enter saves, the caret stays in the
      // editor, and a task never starts with a space — so Space there dictates the next task.
      const emptyEditor = e.target === el && !hasTaskText(el.innerHTML) && !/data-teamio-(employee|patient)id=/.test(el.innerHTML);
      if (spaceBelongsToTarget(e.target) && !emptyEditor) return;
      if (document.querySelector('.modal.show')) return;
      e.preventDefault();
      this._voiceSpaceDownAt = Date.now();
      this.voiceVia = 'hold';
      this.toggleVoice();
    },

    onVoiceKeyup(e) {
      if (!isSpaceKey(e) || this._voiceSpaceDownAt == null) return;
      const held = Date.now() - this._voiceSpaceDownAt;
      this._voiceSpaceDownAt = null;
      e.preventDefault();
      if (held < VOICE_HOLD_MS) {
        // a tap: keep listening until the next Space
        if (this.voiceListening || this.voiceBusy) this.voiceVia = 'tap';
        return;
      }
      if (this.voiceListening) this.stopVoice();
      else if (this.voiceBusy) this._voiceCancelPending = true;
    },

    /** Stop listening and show "Processing" until the recognizer hands back its last words. */
    stopVoice() {
      const rec = this._voiceRec;
      if (!rec || this.voicePhase === 'processing') return;
      this.setVoicePhase('processing');
      // Safety net: if the recognizer never reports its end, finish with what we have.
      this._voiceFinishTimer = setTimeout(() => {
        try { rec.abort(); } catch (_) { /* already stopped */ }
        this.finishVoice();
      }, VOICE_FINISH_TIMEOUT_MS);
      try { rec.stop(); } catch (_) { this.finishVoice(); }
    },

    /** Append the resolved transcript to the draft and re-run the same parse a typed edit gets. */
    applyVoiceTranscript(heard) {
      const el = this.$refs.newtodo;
      if (!el) return;
      const r = resolveVoiceTask(heard, { employees: this.activeEmployees || [], patients: this.patients || [], siteID: this.siteID });
      const existing = (el.innerHTML || '').replace(/<br\s*\/?>$/i, '');
      const hasContent = hasTaskText(existing) || /data-teamio-(employee|patient)id=/.test(existing);
      el.innerHTML = existing + (hasContent ? '&nbsp;' : '') + r.html + '&nbsp;';
      const text = typeof el.innerText === 'string' ? el.innerText : el.textContent;
      this.parseContentForDates(text);
      this.refreshDraft();
      // Leave the editor UNFOCUSED: a caret in it would make the next Space type a space. Space
      // dictates more, Enter saves, Escape discards (onVoiceKeydown), a click edits the text.
      if (document.activeElement === el) el.blur();
      this.voiceAmbiguous = r.ambiguous;
      this.voiceInterim = heard;
      this.setVoicePhase('done');
    },

    /**
     * One-click pick for a dictated name that fits several people: the spoken name (left as plain
     * text in the draft) becomes that person's badge — what choosing them in the # / @ lookup gives.
     * The newest occurrence is replaced (the latest dictation); if the text was edited away, the
     * badge is added at the end. The editor stays unfocused, so Space still dictates.
     */
    pickVoiceCandidate(amb, opt) {
      const el = this.$refs.newtodo;
      if (!el || !amb || !opt) return;
      const html = amb.kind === 'staff'
        ? buildEmployeeBadgeHTML(opt)
        : buildPatientBadgeHTML({ siteID: this.siteID, PatNum: opt.PatNum, LName: opt.LName, FName: opt.FName });
      const tpl = document.createElement('template');
      tpl.innerHTML = html;
      const badge = tpl.content.firstChild;
      const name = new RegExp(`(^|[^A-Za-z0-9])(${String(amb.spoken).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})(?![A-Za-z0-9])`, 'g');
      let hit = null;
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      for (let n = walker.nextNode(); n; n = walker.nextNode()) {
        if (n.parentElement && n.parentElement.closest('[contenteditable="false"]')) continue; // inside a badge
        let m;
        name.lastIndex = 0;
        while ((m = name.exec(n.nodeValue)) !== null) hit = { node: n, at: m.index + m[1].length };
      }
      if (hit) {
        const rest = hit.node.splitText(hit.at);
        rest.nodeValue = rest.nodeValue.slice(String(amb.spoken).length);
        rest.parentNode.insertBefore(badge, rest);
      } else {
        el.appendChild(document.createTextNode(' '));
        el.appendChild(badge);
      }
      this.voiceAmbiguous = this.voiceAmbiguous.filter(a => a !== amb);
      this.refreshDraft();
    },

    // Parse the content for a natural-language date via the shared chrono-simple util.
    // Ported from ToDo.vue::parseContentForDates.
    parseContentForDates(content) {
      this.resetDateInfo();

      if (!content || content.trim() === '') {
        if (this.isInline) this.syncInlineDue();
        return;
      }

      try {
        const parsedResult = simpleParser.parse(content);
        if (parsedResult) {
          this.detectedDateInfo = {
            hasDate: true,
            displayText: parsedResult.text,
            date: parsedResult.date,
            isRecurring: parsedResult.isRecurring,
            frequency: parsedResult.frequency,
            daysOfWeek: parsedResult.daysOfWeek,
            dayOfMonth: parsedResult.dayOfMonth,
            repeatOnMonth: parsedResult.repeatOnMonth,
            isLastDayOfMonth: parsedResult.repeatOnLastDayOfMonth,
          };

          if (parsedResult.date && (parsedResult.date instanceof Date || typeof parsedResult.date === 'string')) {
            const parsed = parsedResult.date instanceof Date ? parsedResult.date : new Date(parsedResult.date);
            if (!isNaN(parsed.getTime())) {
              this.selectedDueDate = parsed;
            }
          }
        }
      } catch (error) {
        console.error('Error calling chrono-simple parse:', error);
        this.resetDateInfo();
      }
      if (this.isInline) this.syncInlineDue();
    },

    // inline: the due date follows the TEXT (classic keeps the DatePicker's last value instead, so
    // deleting "by Friday" there leaves Friday picked). A Due-chip pick beats the text.
    syncInlineDue() {
      if (this.pickedDueDate) {
        this.selectedDueDate = this.pickedDueDate;
        return;
      }
      const d = this.detectedDateInfo.hasDate ? this.detectedDateInfo.date : null;
      const parsed = d instanceof Date ? d : (typeof d === 'string' ? new Date(d) : null);
      this.selectedDueDate = parsed && !isNaN(parsed.getTime()) ? parsed : null;
    },

    resetDateInfo() {
      this.detectedDateInfo = {
        hasDate: false,
        displayText: '',
        date: null,
        isRecurring: false,
        frequency: null,
        daysOfWeek: null,
        dayOfMonth: null,
        repeatOnMonth: null,
        isLastDayOfMonth: false,
      };
    },

    // Enter in the tag box: add to THIS task's tags only. The site-wide task-tags collection
    // is updated at save time by tasks/addPersonalTask, so nothing is persisted on Enter.
    // History: the handler ported from ToDo.vue dispatched tasks/addSiteTaskTag with `{ tag }`
    // while the action reads `tagName` — a silent no-op for as long as it existed. Making it
    // "work" would create a site tag for a task that may never be saved (Saumil, 2026-09-02:
    // option B, remove it). Pinned by tests/component/TaskComposer.spec.js.
    addTag(newTag) {
      if (!this.selectedTags.includes(newTag)) {
        this.selectedTags.push(newTag);
      }
    },
    setCaretAtBeginning() {
      this.$nextTick(() => {
        const el = this.$refs.newtodo;
        if (!el) return;

        // Highlight effect (classic only — the inline editor draws its own teal focus ring)
        const highlight = !this.isInline;
        if (highlight) {
          el.style.borderColor = '#3abf94';
          el.style.boxShadow = '0 0 0 3px rgba(58, 191, 148, 0.3)';
        }

        // jsdom has no Selection/Range API in some configs — guard it (env guard, not a test workaround).
        if (typeof window.getSelection === 'function' && typeof document.createRange === 'function') {
          try {
            const range = document.createRange();
            const sel = window.getSelection();
            range.setStart(el, 0);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
          } catch (_) { /* selection not implemented in this environment */ }
        }

        el.focus();

        if (highlight) {
          setTimeout(() => {
            el.style.borderColor = '';
            el.style.boxShadow = '';
          }, 2000);
        }
      });
    },

    setCaretAtEnd() {
      this.$nextTick(() => {
        const el = this.$refs.newtodo;
        if (!el) return;

        if (typeof window.getSelection === 'function' && typeof document.createRange === 'function') {
          try {
            const range = document.createRange();
            const sel = window.getSelection();
            range.selectNodeContents(el);
            range.collapse(false); // collapse to the END of the contents
            sel.removeAllRanges();
            sel.addRange(range);
          } catch (_) { /* selection not implemented in this environment */ }
        }

        el.focus();
      });
    },

    // Public API: parent components (e.g. My Tasks) own a ref to this composer and
    // call focus() to move the caret into the editor — end of the text when a
    // patient is seeded, otherwise the beginning (with the highlight). Mirrors
    // mounted()'s autofocus branch so the behaviour is identical either way.
    focus() {
      if (this.seedPatient) {
        this.setCaretAtEnd();
      } else {
        this.setCaretAtBeginning();
      }
    },

    // Seed the editor with the patient badge (+ a trailing space to type after).
    seedEditor() {
      const el = this.$refs.newtodo;
      if (!el || !this.seedPatientForBadge) return;
      el.innerHTML = buildPatientBadgeHTML(this.seedPatientForBadge) + '&nbsp;';
      this.refreshDraft();
      this.setCaretAtEnd();
    },

    resetEditor() {
      const el = this.$refs.newtodo;
      if (el) el.innerHTML = '';
      this.selectedTags = [];
      this.selectedDueDate = null;
      this.newTodoContent = '';
      this.resetDateInfo();
      // inline: the chips go back to their defaults; `focused` is left as it is.
      this.draftHtml = '';
      this.pickedDueDate = null;
      this.dueOpen = false;
      // A dictation still in progress keeps its pill; a finished one clears with the draft.
      if (!this.voiceListening && !this.voiceBusy) {
        this.setVoicePhase('');
        this.voiceInterim = '';
        this.voiceAmbiguous = [];
      }
      if (this.seedPatient) {
        this.seedEditor();
      } else {
        this.setCaretAtBeginning();
      }
    },

    async saveTodo() {
      // Ensure settings are loaded before proceeding — ported verbatim from
      // ToDo.vue:1211-1234 (it only logs and retries getSettings once).
      if (!this.allSettings || !this.allSettings.GENERAL_TIMEZONE) {
        console.warn('Settings not available. This should not happen after login. Current state:', {
          siteID: this.siteID,
          settingsLength: this.settings?.length || 0,
          allSettingsExists: !!this.allSettings,
          hasTimezone: !!this.allSettings?.GENERAL_TIMEZONE,
        });

        // If settings are missing, try to reload them once
        if (!this.settings || this.settings.length === 0) {
          console.log('Attempting to reload settings for siteID:', this.siteID);
          try {
            await this.getSettings({ siteID: this.siteID });
          } catch (error) {
            console.error('Error reloading settings:', error);
          }
        }

        // If still no settings, continue with fallback but log the issue
        if (!this.allSettings || !this.allSettings.GENERAL_TIMEZONE) {
          console.error('Settings still not available. Using fallback timezone. This indicates a data loading issue.');
        }
      }

      const el = this.$refs.newtodo;
      if (!el) return;
      const text = typeof el.innerText === 'string' ? el.innerText : el.textContent;
      let html = el.innerHTML;
      this.parseContentForDates(text); // re-parse in case onInput didn't fire

      const problem = validateDraft(html);
      if (problem) {
        this.$toast.error(problem);
        return;
      }

      this.saving = true;
      try {
        // buildNewTask / ensurePatientTagged live INSIDE the try: if either throws
        // (e.g. a bad seed, or missing task fields), the catch below turns it into a
        // visible toast + `error` emit instead of an unhandled rejection. extractApiError
        // downgrades any internal message to the generic fallback.
        if (this.seedPatient && this.enforcePatient) {
          html = ensurePatientTagged(html, this.seedPatientForBadge);
        }

        // inline: no date typed or picked → the caller's default (Upcoming: tomorrow). buildNewTask
        // already falls back to today, so defaultDue=today changes nothing.
        const useDefaultDue = this.isInline && !this.selectedDueDate && !this.detectedDateInfo.hasDate && !!this.defaultDue;

        const { task, notifications } = buildNewTask({
          contentHTML: html,
          siteID: this.siteID,
          userID: this.userID,
          userName: this.user && this.user.Name,
          timezone: this.officeTimezone,
          maxSortID: this.maxSortId,
          selectedTags: this.selectedTags,
          detectedDateInfo: this.detectedDateInfo,
          selectedDueDate: useDefaultDue ? this.defaultDue : this.selectedDueDate,
        });

        // Emit the built task BEFORE the await so the parent can push it into its lists
        // optimistically. Pushing after the save races the Firestore snapshot (which has
        // already replaced those arrays) and leaves an id-less duplicate. Only fires once
        // the draft has passed validation, so a refused save never emits it.
        this.$emit('saving', task);

        const taskId = await this.addPersonalTask({ task, notification: notifications });
        this.resetEditor();
        this.$emit('saved', taskId, task);
      } catch (err) {
        this.$emit('error', err);
        this.$toast.error(extractApiError(err, 'Could not add the task. Please try again.'));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<!--
  Moved from ToDo.vue's scoped block (2026-09-02). ALL of the composer's sizing lives here now
  (2026-09-11): the "unscoped base rules" ToDo.vue was said to keep for PracticePulse sat inside a
  commented-out style block and never compiled — PracticePulse's composers style themselves
  (.practice-pulse-details-page .task-input-section …). ToDo.vue's one live global block only adds
  a min-height/margin to .ToDo__addNew and position: relative to .task-input-container.
-->
<style scoped lang="scss">
@import '../Followups/mixins'; // $fu-* tokens (teal, static hex) for variant="inline" — emits no CSS

.task-input-wrapper {
  margin-bottom: 1rem;
}

.task-input-wrapper--entrance {
  animation: taskInputEntrance 0.6s ease-out;
}

@keyframes taskInputEntrance {
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  50% {
    opacity: 1;
    transform: translateY(0);
    box-shadow: 0 0 0 4px rgba(58, 191, 148, 0.35);
    border-radius: 8px;
  }
  100% {
    box-shadow: none;
  }
}

.task-input-section {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  flex-wrap: wrap;
}

/* ------------------------------------------------------------------------------------------------
   The layout is CONTAINER-driven (2026-09-11), never viewport-driven.

   The composer is either ONE LINE — editor | due date | tags | Add Task | actions — or STACKED — the
   editor owns the whole first line and the controls wrap below it. Which one is decided by the width
   of .task-input-container itself, through the editor's flex-basis:

       flex-basis: calc((var(--composer-row-min) - 100%) * 99999)

   container >= --composer-row-min → the calc is negative → the basis clamps to 0 → the editor grows
   into whatever the fixed-width siblings leave. Those take ~640px (date 160 + tags 250 + Add Task
   ~106 + the My Tasks report/settings icons + gaps), so at 1200px the editor still gets >= ~560px —
   about half the line. Below that the editor is better off with the whole first line.
   container <  --composer-row-min → the calc is hugely positive → max-width: 100% caps it at the
   full line → every sibling wraps to the next line. (Every Layout's "Switcher" pattern.)

   Why 99999 and not the pattern's usual 999: here only the EDITOR switches, so just under the
   threshold its basis must already exceed the line minus the due date. With 999, a container 1px
   under (1199px) gives a 999px basis and the date squeezes onto the editor's line while tags and
   Add Task wrap — measured in Chrome with the minified prod rule. Browsers lay out in 1/64px units,
   so the multiplier must be > threshold x 64 (76,800 at 1200px) for no container width to land in
   that half-switched state. Oversized results are harmless: max-width: 100% clamps them.

   Why not @media: the old breakpoints keyed off the VIEWPORT. The same composer inside the 800px
   Create Task panel on a 1400px screen ran the one-row layout (the 2026-09-02 sliver), and on My
   Tasks every 1025–1440px viewport hit the desktop `flex-wrap: nowrap` block: the 2026-09-11
   customer report was a 1280px browser (a 1920x1080 laptop at 150% Windows scaling is exactly that)
   painting a 257px editor beside a 500px tag box. Why not @container: `container-type` adds layout containment — a new stacking context —
   and the # / @ mention panel (.atwho-panel, absolute, z-index 11110) lives INSIDE this element; it
   would paint beneath the filter row that follows the composer. The calc needs no containment.

   Pinned by cypress/node-tests/task-composer-layout-contract.test.js (source) and
   cypress/e2e/task-composer-layout.cy.js (rendered geometry at 390–1920px).
   ------------------------------------------------------------------------------------------------ */
.task-input-container {
  --composer-row-min: 1200px;
  display: flex;
  align-items: center;
  flex: 1;
  gap: 0.5rem;
  min-width: 0; /* Allow shrinking */
  flex-wrap: wrap;
}

.newtodo {
  flex-grow: 1;
  flex-shrink: 1;
  flex-basis: calc((var(--composer-row-min) - 100%) * 99999);
  /* Defense in depth (2026-09-03 floor, kept): the switch already guarantees >= ~560px on one line,
     so this only bites if a future sibling eats the line — then the row wraps instead of starving. */
  min-width: 200px;
  max-width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  background-color: white;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  min-height: 38px;
  transition: all 0.2s ease-in-out;
}

/* The tag box MUST carry its own basis here. vue-multiselect's stylesheet gives its root
   `width: 100%`, and other pages leak global `.multiselect` rules (Settings.scss caps it at 500px);
   the rule that was meant to fix it at 250px sat in a commented-out style block in ToDo.vue and
   never compiled. Without a scoped basis the box claims the line first and the editor gets the rest. */
.tag-input {
  flex: 0 0 250px;
  width: 250px;
  min-width: 0;
  max-width: 100%;
}

/* Forced stacked layout (the Create Task panel passes layout="stacked"): the same shape the switch
   produces on a narrow container, plus the tag box fills what is left of the second line instead
   of leaving a gap after Add Task. */
.task-composer--stacked .newtodo {
  flex: 1 1 100%;
}

.task-composer--stacked .tag-input {
  flex: 1 1 200px;
  width: auto;
  min-width: 200px;
  max-width: none;
}

.newtodo:focus {
  outline: none;
  border-color: #3abf94;
  box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.3);
}

/* Placeholder for the contenteditable editor. Moved from ToDo.vue's scoped block
   (2026-09-02) when the composer became a child component — ToDo.vue's scoped
   `[contenteditable][placeholder]` rule no longer reaches this element. */
.newtodo[placeholder]:empty:before {
  content: attr(placeholder);
  color: #9ca3af;
  background-color: transparent;
  pointer-events: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 100%;
}

@media (max-width: 480px) {
  .newtodo[placeholder]:empty:before {
    content: "Add a task...";
    font-size: 13px;
  }
}

@media (min-width: 481px) and (max-width: 768px) {
  .newtodo[placeholder]:empty:before {
    content: "Add your task here... (@ to assign, # for patients)";
    font-size: 13px;
  }
}

.due-date-picker {
  flex: 0 0 auto;
  width: 160px;
  min-width: 140px;
  height: 38px;
  position: relative;
  z-index: 25;
}

:deep(.due-date-picker) {
  .mx-input-wrapper {
    height: 38px;
    overflow: hidden;
  }

  input.mx-input,
  .due-date-input {
    width: 100% !important;
    height: 38px !important;
    border: 1px solid #d1d5db !important;
    border-radius: 4px !important;
    background: white !important;
    padding: 4px 30px 4px 10px !important;
    font-size: 13px !important;
    color: #374151 !important;
    box-sizing: border-box !important;
    transition: all 0.2s ease-in-out;

    &::placeholder {
      color: #9ca3af !important;
    }

    &:focus {
      border-color: #3abf94 !important;
      box-shadow: 0 0 0 3px rgba(58, 191, 148, 0.3) !important;
    }
  }

  .mx-icon-calendar {
    color: #9ca3af;
  }

  .mx-icon-clear {
    color: #9ca3af;

    &:hover {
      color: #ef4444;
    }
  }
}

.add-task-button {
  background-color: #3abf94;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  height: 38px;
  white-space: nowrap;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  flex: 0 0 auto; /* Don't grow or shrink */
}

.add-task-button:hover {
  background-color: #34a884;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.add-task-button:active {
  background-color: #2e9775;
  transform: translateY(1px);
}

/* Tablet (768–1024px) needs no block of its own any more: at those widths the container is below
   --composer-row-min, so the switch above already gives the editor the first line. */

/* ============================================ */
/* MOBILE (< 768px) */
/* ============================================ */
@media (max-width: 767px) {
  .task-input-wrapper {
    flex-direction: column;
    gap: 8px;
  }

  .task-input-section {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
  }

  .task-input-container {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
    flex: 1 1 auto;
  }

  .newtodo {
    flex: 1 1 100%;
    min-width: 0;
    font-size: 13px;
  }

  .due-date-picker {
    flex: 1 1 100%;
    min-width: 0;
    max-width: 100%;
  }

  .tag-input {
    flex: 1 1 100%;
    min-width: 0 !important;
    max-width: 100%;
  }

  .add-task-button {
    flex: 1 1 auto;
    display: flex;
    justify-content: center;
  }
}

/* ============================================ */
/* SMALL PHONE (< 480px) */
/* ============================================ */
@media (max-width: 480px) {
  .newtodo {
    font-size: 12px;
    padding: 6px 10px;
    min-height: 34px;
  }

  .due-date-picker {
    :deep(input.mx-input),
    :deep(.due-date-input) {
      height: 34px !important;
      font-size: 12px !important;
    }
  }

  .add-task-button {
    font-size: 13px;
    height: 34px;
    padding: 6px 12px;
  }
}

/* ================================================================================================
   variant="inline" — the redesigned Tasks page's one-line composer (design "Tasks Page Options" 2a).
   Every rule is FLAT and prefixed .task-composer--inline, so the classic rules above (and the
   source-level contract in cypress/node-tests/task-composer-layout-contract.test.js) are untouched.
   The editor is the only flex child left in .task-input-container, so the switcher basis above
   simply gives it the whole line at every width. Teal tokens only (never iris).
   ================================================================================================ */
.task-composer--inline {
  margin-bottom: 0;
}

.task-composer--inline .task-input-container {
  position: relative;
}

.task-composer--inline .newtodo {
  min-height: 42px;
  margin-bottom: 0;
  padding: 9px 14px 9px 40px;
  border: 1px solid $fu-border;
  border-radius: $fu-radius-md;
  background-color: $fu-surface;
  box-shadow: $fu-shadow-xs;
  font-size: 15px;
  line-height: 22px;
  color: $fu-text;
  overflow-wrap: anywhere;
  transition: border-color $fu-dur-fast $fu-ease-out, box-shadow $fu-dur-fast $fu-ease-out;
}

.task-composer--inline .newtodo:focus {
  outline: none;
  border-color: $fu-brand-600;
  box-shadow: 0 0 0 3px rgba($fu-brand-600, 0.18);
}

/* The page passes its own short placeholder; the classic per-viewport placeholder texts do not apply. */
.task-composer--inline .newtodo[placeholder]:empty:before {
  content: attr(placeholder);
  color: $fu-neutral-400;
  font-size: 15px;
}

.task-composer--inline .tc-inline__lead {
  position: absolute;
  top: 12px;
  left: 13px;
  display: flex;
  color: $fu-neutral-400;
  pointer-events: none;
}

.task-composer--inline .newtodo:focus ~ .tc-inline__lead {
  color: $fu-brand-600;
}

/* ---- Voice prototype: mic at the right end of the editor + the status line under it ---------- */
.task-composer--inline.task-composer--voice .newtodo {
  padding-right: 44px;
}

.task-composer--inline .tc-inline__mic {
  position: absolute;
  top: 6px;
  right: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: $fu-neutral-400;
  cursor: pointer;
}

.task-composer--inline .tc-inline__mic:hover {
  color: $fu-brand-600;
  background: rgba($fu-brand-600, 0.08);
}

.task-composer--inline .tc-inline__mic:focus {
  outline: none;
}

.task-composer--inline .tc-inline__mic:focus-visible {
  box-shadow: $fu-ring;
}

.task-composer--inline .tc-inline__mic--on,
.task-composer--inline .tc-inline__mic--on:hover {
  color: $fu-danger;
  background: $fu-danger-subtle;
}

.task-composer--inline .tc-inline__mic:disabled {
  opacity: 0.5;
  cursor: progress;
}

/* The mic breathes while it is open, so the state is visible even when the eye is on the text. */
.task-composer--inline .tc-inline__mic--on {
  animation: tc-mic-pulse 1.4s ease-out infinite;
}

@keyframes tc-mic-pulse {
  0%   { box-shadow: 0 0 0 0 rgba($fu-danger, 0.45); }
  70%  { box-shadow: 0 0 0 8px rgba($fu-danger, 0); }
  100% { box-shadow: 0 0 0 0 rgba($fu-danger, 0); }
}

/* ---- Dictation status pill ---------------------------------------------------------------------- */
.task-composer--inline .tc-voice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 8px;
  padding: 8px 12px;
  border: 1px solid $fu-neutral-200;
  border-radius: $fu-radius-md;
  background: $fu-neutral-50;
  font-size: 13px;
  line-height: 1.45;
  color: $fu-neutral-700;
  overflow-wrap: anywhere;
}

.task-composer--inline .tc-voice--listening {
  border-color: $fu-danger-border;
  background: $fu-danger-subtle;
}

.task-composer--inline .tc-voice--preparing,
.task-composer--inline .tc-voice--processing {
  border-color: $fu-brand-200;
  background: $fu-brand-50;
}

.task-composer--inline .tc-voice--done {
  border-color: $fu-brand-200;
  background: $fu-success-subtle;
}

.task-composer--inline .tc-voice--error {
  border-color: $fu-danger-border;
  background: $fu-surface;
}

.task-composer--inline .tc-voice__icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 19px; /* one line of the title, so the icon sits on it */
  color: $fu-neutral-500;
}

.task-composer--inline .tc-voice--done .tc-voice__icon { color: $fu-success; }
.task-composer--inline .tc-voice--error .tc-voice__icon { color: $fu-danger; }

.task-composer--inline .tc-voice__dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: $fu-danger;
  animation: tc-voice-blink 1s ease-in-out infinite;
}

@keyframes tc-voice-blink {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%      { opacity: 0.35; transform: scale(0.8); }
}

.task-composer--inline .tc-voice__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid $fu-brand-200;
  border-top-color: $fu-brand-600;
  border-radius: 50%;
  animation: tc-voice-spin 0.8s linear infinite;
}

@keyframes tc-voice-spin {
  to { transform: rotate(360deg); }
}

.task-composer--inline .tc-voice__body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.task-composer--inline .tc-voice__head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 2px 12px;
}

.task-composer--inline .tc-voice__title {
  color: $fu-neutral-800;
  font-weight: $fu-fw-semibold;
}

.task-composer--inline .tc-voice--listening .tc-voice__title { color: $fu-danger-text; }
.task-composer--inline .tc-voice--processing .tc-voice__title,
.task-composer--inline .tc-voice--preparing .tc-voice__title { color: $fu-brand-700; }
.task-composer--inline .tc-voice--done .tc-voice__title { color: $fu-success; }
.task-composer--inline .tc-voice--error .tc-voice__title { color: $fu-danger-text; }

.task-composer--inline .tc-voice__hint {
  color: $fu-neutral-500;
  font-size: 12px;
}

.task-composer--inline .tc-voice__detail {
  color: $fu-neutral-700;
  font-style: italic;
}

.task-composer--inline .tc-voice--done .tc-voice__detail {
  font-style: normal;
}

.task-composer--inline .tc-voice__warn {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  color: $fu-neutral-800;
}

.task-composer--inline .tc-voice__ask {
  font-weight: $fu-fw-semibold;
}

.task-composer--inline .tc-voice__pick {
  max-width: 100%;
  padding: 2px 10px;
  border: 1px solid $fu-brand-200;
  border-radius: 999px;
  background: $fu-surface;
  color: $fu-brand-700;
  font-size: 12px;
  line-height: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.task-composer--inline .tc-voice__pick:hover,
.task-composer--inline .tc-voice__pick:focus-visible {
  border-color: $fu-brand-600;
  background: $fu-brand-200;
}

.task-composer--inline .tc-voice__more {
  color: $fu-neutral-500;
  font-size: 12px;
}

@media (prefers-reduced-motion: reduce) {
  .task-composer--inline .tc-inline__mic--on,
  .task-composer--inline .tc-voice__dot,
  .task-composer--inline .tc-voice__spinner {
    animation: none;
  }
}

/* ---- Chips row ---------------------------------------------------------------------------------- */
.task-composer--inline .tc-inline__chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  min-width: 0;
}

.task-composer--inline .tc-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  margin: 0;
  padding: 0 10px;
  border: 1px dashed $fu-border-strong;
  border-radius: 8px;
  background: transparent;
  font-family: inherit;
  font-size: 12.5px;
  line-height: 1;
  color: $fu-neutral-500;
  white-space: nowrap;
  max-width: 100%;
}

.task-composer--inline button.tc-chip {
  cursor: pointer;
}

.task-composer--inline button.tc-chip:hover {
  border-color: $fu-brand-300;
}

.task-composer--inline .tc-chip:focus {
  outline: none;
}

.task-composer--inline .tc-chip:focus-visible {
  box-shadow: $fu-ring;
}

.task-composer--inline .tc-chip__icon {
  flex: 0 0 auto;
}

.task-composer--inline .tc-chip__value {
  color: $fu-neutral-600;
  font-weight: $fu-fw-semibold;
  overflow: hidden;
  text-overflow: ellipsis;
}

.task-composer--inline .tc-chip--set {
  border: 1px solid transparent;
  background: $fu-brand-50;
  color: $fu-brand-700;
}

.task-composer--inline button.tc-chip--set:hover {
  border-color: $fu-brand-200;
}

.task-composer--inline .tc-chip--set .tc-chip__value {
  color: $fu-brand-700;
  font-weight: $fu-fw-bold;
}

/* The Due chip carries a native date input (never b-form-datepicker), invisible, laid over the chip so
   showPicker() anchors the calendar to it. Clicks go to the button (pointer-events: none). */
.task-composer--inline .tc-chip-host {
  position: relative;
  display: inline-flex;
  max-width: 100%;
}

.task-composer--inline .tc-chip__date {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
  opacity: 0;
  pointer-events: none;
}

.task-composer--inline .tc-inline__hint {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  font-size: 12px;
  color: $fu-neutral-400;
  white-space: nowrap;
}

/* ---- @ / # suggestion popover (At.vue's panel, restyled): anchored under the input at the composer's
   left edge (the design's 340px card), not at the caret — so it can never hang past the page. The
   panel's `top` stays At's caret-relative value, so a wrapped second line still opens below its line. */
.task-composer--inline ::v-deep .atwho-panel {
  left: 0 !important;
}

.task-composer--inline ::v-deep .atwho-view {
  top: 38px;
  bottom: auto;
  left: 0;
  width: 340px;
  min-width: 0;
  max-width: calc(100vw - 32px);
  max-height: 300px;
  padding: 6px;
  background-color: $fu-surface;
  border: 1px solid $fu-border;
  border-radius: 12px;
  box-shadow: $fu-shadow-lg;
  color: $fu-text;
}

.task-composer--inline ::v-deep .atwho-view::before {
  display: block;
  padding: 6px 10px 4px;
  font-size: 11px;
  font-weight: $fu-fw-bold;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: $fu-neutral-500;
}

.task-composer--inline ::v-deep .at-staff > .atwho-panel .atwho-view::before {
  content: 'Assign to';
}

.task-composer--inline ::v-deep .at-patients > .atwho-panel .atwho-view::before {
  content: 'Patient';
}

.task-composer--inline ::v-deep .atwho-ul {
  max-height: none;
}

.task-composer--inline ::v-deep .atwho-li {
  height: auto;
  min-height: 34px;
  padding: 7px 10px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: $fu-fw-semibold;
  color: $fu-text;
  cursor: pointer;
}

.task-composer--inline ::v-deep .atwho-li:hover {
  background: $fu-neutral-50;
}

.task-composer--inline ::v-deep .atwho-li.atwho-cur {
  background: $fu-brand-50;
  color: $fu-brand-700;
}

/* A suggestion row: name left, "N open" right (design 2a). */
.task-composer--inline ::v-deep .tc-at-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
}

.task-composer--inline ::v-deep .tc-at-item__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.task-composer--inline ::v-deep .tc-at-item__sub {
  flex: none;
  margin-left: auto;
  font-size: 12px;
  font-weight: $fu-fw-regular;
  color: $fu-neutral-500;
  white-space: nowrap;
}
</style>
